import { useTranslation } from 'react-i18next';

export default function TaxOfficeSection() {
  const { t, i18n } = useTranslation();
  const isJa = i18n.language === 'ja' || i18n.language.startsWith('ja');

  return (
    <section
      id="tax-office"
      className="py-20 bg-gradient-to-b from-amber-50 via-stone-50 to-amber-50 font-serif"
      aria-label="横田税務会計事務所 アクセス"
    >
      <div className="absolute left-0 right-0 h-2 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-60" />

      <div className="max-w-7xl mx-auto px-6">
        {/* セクションヘッダー */}
        <div className="text-center mb-14">
          <span
            className="inline-flex items-center gap-2 bg-[#8b2500]/10 text-[#8b2500] text-xs font-bold tracking-widest uppercase px-5 py-2 rounded-full mb-4 border border-[#8b2500]/20"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            <i className="ri-building-2-line text-base"></i>
            {t('common:taxOffice.badge')}
          </span>

          <h2
            className="text-4xl md:text-5xl font-bold text-stone-800 mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            <span className="text-[#8b2500]">◆</span>{' '}
            {t('common:taxOffice.sectionTitle')}{' '}
            <span className="text-[#8b2500]">◆</span>
          </h2>

          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a84c]" />
            <span className="text-[#8b2500] text-xl">◇</span>
            <div className="h-px w-32 bg-[#c9a84c]" />
            <span className="text-[#8b2500] text-xl">◇</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a84c]" />
          </div>

          <p
            className="text-base text-stone-600 max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            {t('common:taxOffice.sectionSubtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* 左：NAP情報カード */}
          <div className="space-y-5">
            <article
              className="relative bg-white rounded-2xl border-2 border-[#8b2500]/30 shadow-lg p-8"
              itemScope
              itemType="https://schema.org/AccountingService"
            >
              <div className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-[#c9a84c]" />
              <div className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-[#c9a84c]" />

              {/* 事務所名 */}
              <div className="flex items-center gap-3 mb-6 pb-5 border-b border-[#c9a84c]/30">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-xl shadow-md flex-shrink-0">
                  <i className="ri-building-2-line text-[#c9a84c] text-2xl"></i>
                </div>
                <div>
                  <p className="text-xs text-stone-500 tracking-widest uppercase mb-0.5">
                    {t('common:taxOffice.addressLabel')}
                  </p>
                  <h3
                    className="text-xl font-bold text-stone-900 leading-tight"
                    style={{ fontFamily: "'Noto Serif JP', serif" }}
                    itemProp="name"
                  >
                    {t('common:taxOffice.businessName')}
                  </h3>
                  {!isJa && (
                    <p className="text-sm text-stone-500 mt-0.5">横田税務会計事務所</p>
                  )}
                  {isJa && (
                    <p className="text-sm text-stone-500 mt-0.5">{t('common:taxOffice.businessNameEn')}</p>
                  )}
                </div>
              </div>

              {/* NAP リスト */}
              <div className="space-y-5" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                {/* 住所 */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-50 border border-[#8b2500]/20 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-2-line text-[#8b2500] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                      {t('common:taxOffice.addressLabel')}
                    </p>
                    <address className="not-italic">
                      <p
                        className="text-base font-semibold text-stone-900"
                        style={{ fontFamily: "'Noto Serif JP', serif" }}
                        itemProp="streetAddress"
                      >
                        {isJa ? t('common:taxOffice.addressJa') : t('common:taxOffice.addressEn')}
                      </p>
                      <p className="text-sm text-stone-500 mt-0.5">
                        {isJa ? t('common:taxOffice.addressEn') : t('common:taxOffice.addressJa')}
                      </p>
                    </address>
                    <meta itemProp="addressLocality" content="Ota City" />
                    <meta itemProp="addressRegion" content="Tokyo" />
                    <meta itemProp="addressCountry" content="JP" />
                  </div>
                </div>

                {/* サービスエリア */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-50 border border-[#8b2500]/20 rounded-lg flex-shrink-0">
                    <i className="ri-radar-line text-[#8b2500] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                      {t('common:taxOffice.serviceAreaLabel')}
                    </p>
                    <p
                      className="text-base font-semibold text-stone-900"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    >
                      {t('common:taxOffice.serviceArea')}
                    </p>
                    {!isJa && (
                      <p className="text-sm text-stone-500 mt-0.5">大田区・東京都全域</p>
                    )}
                  </div>
                </div>

                {/* アクセス */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-50 border border-[#8b2500]/20 rounded-lg flex-shrink-0">
                    <i className="ri-train-line text-[#8b2500] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                      {t('common:taxOffice.accessLabel')}
                    </p>
                    <p
                      className="text-base font-semibold text-stone-900"
                      style={{ fontFamily: "'Noto Serif JP', serif" }}
                    >
                      {isJa ? t('common:taxOffice.accessJa') : t('common:taxOffice.accessEn')}
                    </p>
                    <p className="text-sm text-stone-500 mt-0.5">
                      {isJa ? t('common:taxOffice.accessEn') : t('common:taxOffice.accessJa')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Google Maps ボタン */}
              <div className="mt-6 pt-5 border-t border-[#c9a84c]/30">
                <a
                  href="https://maps.google.com/?q=東京都大田区東馬込1-12-12"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#8b2500] to-[#a52a00] text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  <i className="ri-map-2-line text-base"></i>
                  {t('common:taxOffice.openMapBtn')}
                </a>
              </div>
            </article>

            {/* 税務サポート注記カード */}
            <div className="relative bg-gradient-to-br from-amber-50 to-stone-50 rounded-2xl border-2 border-[#c9a84c]/40 p-6">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#8b2500]" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#8b2500]" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#8b2500]" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#8b2500]" />

              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-xl shadow-md flex-shrink-0">
                  <i className="ri-file-text-line text-[#c9a84c] text-xl"></i>
                </div>
                <h4
                  className="text-base font-bold text-stone-800"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  {t('common:taxOffice.supportTitle')}
                </h4>
              </div>

              <ul
                className="space-y-2 text-sm text-stone-700"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {(t('common:taxOffice.supportItems', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-[#8b2500] flex-shrink-0 mt-0.5"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 pt-4 border-t border-[#c9a84c]/30">
                <p className="text-xs text-stone-500 leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  {t('common:taxOffice.disclaimer')}
                </p>
              </div>
            </div>
          </div>

          {/* 右：Google マップ埋め込み */}
          <div className="space-y-5">
            <div className="relative rounded-2xl overflow-hidden shadow-xl border-4 border-[#8b2500]/30">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#c9a84c] z-10" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#c9a84c] z-10" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#c9a84c] z-10" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#c9a84c] z-10" />

              <iframe
                src="https://maps.google.com/maps?q=東京都大田区東馬込1-12-12&t=&z=17&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="420"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="横田税務会計事務所 — 東京都大田区東馬込1-12-12"
              />
            </div>

            {/* 写真スペース */}
            <div className="relative rounded-2xl overflow-hidden border-2 border-[#8b2500]/20 shadow-md">
              <img
                src="https://public.readdy.ai/ai/img_res/45a2f82d4cba60f8553496372b642d07.jpg"
                alt={t('common:taxOffice.officeImageAlt')}
                className="w-full h-48 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p
                  className="text-white text-sm font-semibold"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  {t('common:taxOffice.officeImageCaption')}
                </p>
                <p className="text-stone-300 text-xs mt-0.5">
                  {t('common:taxOffice.officeImageSubCaption')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
